import { useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';

import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import Meta from '../../components/layout/Meta';
import DiscountFormModal from '../../components/admin/DiscountFormModal';
import { useDeleteDiscountMutation, useGetDiscountsQuery } from '../../slices/discountApiSlice';
import useIsMobile from '../../utils/useIsMobile';

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('es-EC');
};

const parseBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1';

const DiscountListScreen = () => {
    const isMobile = useIsMobile();
    const [onePerUserFilter, setOnePerUserFilter] = useState('');
    const queryFilters = useMemo(() => ({ onePerUser: onePerUserFilter }), [onePerUserFilter]);

    const {
        data: discounts = [],
        isLoading: discountLoading,
        isFetching: discountFetching,
        error: discountError,
        refetch,
    } = useGetDiscountsQuery(queryFilters);
    const [deleteDiscount, { isLoading: isDeleting }] = useDeleteDiscountMutation();

    const [showModal, setShowModal] = useState(false);
    const [initialData, setInitialData] = useState(null);

    const visibleDiscounts = useMemo(() => {
        if (!Array.isArray(discounts)) return [];
        return [...discounts].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }, [discounts]);

    const openCreateModal = () => {
        setInitialData(null);
        setShowModal(true);
    };

    const openEditModal = (discount) => {
        setInitialData(discount);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setInitialData(null);
    };

    const handleDelete = async (discountId) => {
        if (!window.confirm('¿Seguro que deseas eliminar este descuento?')) return;

        try {
            await deleteDiscount(discountId).unwrap();
            toast.success('Descuento eliminado correctamente.');
        } catch (error) {
            toast.error(error?.data?.message || error?.error || 'No se pudo eliminar el descuento.');
        }
    };

    const discountErrorMessage = discountError?.data?.message || discountError?.error;

    return (
        <Container fluid>
            <Meta title="Descuentos" />

            <Row className="align-items-center mb-3">
                <Col>
                    <h1 className="mb-0">Descuentos</h1>
                    <small className="text-muted">Gestiona descuentos por empresa con control de vigencia y uso.</small>
                </Col>
                <Col xs="auto">
                    <Button
                        onClick={openCreateModal}
                        style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                        className="d-inline-flex align-items-center gap-2"
                    >
                        <FaPlus /> Nuevo descuento
                    </Button>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col xs={12} md={4} lg={3}>
                    <Form.Group controlId="discount-one-per-user-filter">
                        <Form.Label column={false} className="mb-1">Uso único por usuario</Form.Label>
                        <Form.Select
                            value={onePerUserFilter}
                            onChange={(e) => setOnePerUserFilter(e.target.value)}
                        >
                            <option value="">Todos</option>
                            <option value="true">Sí</option>
                            <option value="false">No</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {discountLoading && visibleDiscounts.length === 0 ? <Loader /> : null}
            {discountErrorMessage ? <Message variant="danger">{discountErrorMessage}</Message> : null}

            {!discountLoading && !discountFetching && visibleDiscounts.length === 0 ? (
                <Card className="shadow-sm border-0">
                    <Card.Body className="text-center py-4 text-muted">
                        No hay descuentos creados.
                    </Card.Body>
                </Card>
            ) : null}

            {visibleDiscounts.length > 0 ? (
                <>
                    {!isMobile ? (
                        <Card className="shadow-sm border-0">
                            <Card.Body className="p-0">
                                <Table responsive hover className="mb-0 align-middle">
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Tipo</th>
                                            <th>Valor</th>
                                            <th>Compra mínima</th>
                                            <th>Expira</th>
                                            <th>Empresa</th>
                                            <th>Límite</th>
                                            <th>Uso único por usuario</th>
                                            <th>Estado</th>
                                            <th className="text-end">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleDiscounts.map((discount) => (
                                            <tr key={discount._id}>
                                                <td className="fw-semibold">{discount.code}</td>
                                                <td>{discount.type === 'fixed' ? 'Fijo' : 'Porcentaje'}</td>
                                                <td>{Number(discount.value || 0).toFixed(2)}</td>
                                                <td>${Number(discount.minPurchaseAmount || 0).toFixed(2)}</td>
                                                <td>{formatDate(discount.expiresAt)}</td>
                                                <td>{discount.companyRuc || '-'}</td>
                                                <td>{discount.usageLimit ?? '-'}</td>
                                                <td>{parseBoolean(discount.onePerUser) ? 'Sí' : 'No'}</td>
                                                <td>
                                                    {discount.isActive ? (
                                                        <Badge bg="success">Activo</Badge>
                                                    ) : (
                                                        <Badge bg="secondary">Inactivo</Badge>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <div className="d-inline-flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            onClick={() => openEditModal(discount)}
                                                            aria-label={`Editar ${discount.code}`}
                                                        >
                                                            <FaEdit />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            onClick={() => handleDelete(discount._id)}
                                                            aria-label={`Eliminar ${discount.code}`}
                                                            disabled={isDeleting}
                                                        >
                                                            {isDeleting ? <Spinner animation="border" size="sm" /> : <FaTrash />}
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div className="discount-cards-container">
                            {visibleDiscounts.map((discount) => (
                                <Card key={discount._id} className="mb-3 shadow-sm border-0 rounded-3">
                                    <Card.Body className="p-3">
                                        {/* Header: Code + Actions */}
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div className="flex-grow-1">
                                                <div
                                                    className="fw-bold fs-6 mb-1"
                                                    style={{ color: '#b5430f' }}
                                                >
                                                    {discount.code}
                                                </div>
                                                <Badge bg="light" text="dark" className="border">
                                                    {discount.type === 'fixed' ? 'Fijo' : 'Porcentaje'}
                                                </Badge>
                                                {discount.isActive ? (
                                                    <Badge bg="success" className="ms-1">
                                                        Activo
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="secondary" className="ms-1">
                                                        Inactivo
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="d-flex gap-2 flex-shrink-0">
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    className="border"
                                                    onClick={() => openEditModal(discount)}
                                                    aria-label={`Editar ${discount.code}`}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    className="border border-danger text-danger"
                                                    onClick={() => handleDelete(discount._id)}
                                                    aria-label={`Eliminar ${discount.code}`}
                                                    disabled={isDeleting}
                                                >
                                                    {isDeleting ? <Spinner animation="border" size="sm" /> : <FaTrash />}
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Details Grid */}
                                        <div className="row g-2 small mb-3">
                                            <div className="col-6">
                                                <div className="text-muted mb-1">Valor</div>
                                                <div className="fw-bold" style={{ color: '#b5430f', fontSize: '1.1rem' }}>
                                                    {Number(discount.value || 0).toFixed(2)}
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-muted mb-1">Compra mínima</div>
                                                <div className="fw-bold">${Number(discount.minPurchaseAmount || 0).toFixed(2)}</div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-muted mb-1">Límite</div>
                                                <div className="fw-bold">{discount.usageLimit ?? '-'}</div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-muted mb-1">Uso único/usuario</div>
                                                <div className="fw-bold">
                                                    <Badge bg={parseBoolean(discount.onePerUser) ? 'warning' : 'light'} text={parseBoolean(discount.onePerUser) ? 'dark' : 'dark'}>
                                                        {parseBoolean(discount.onePerUser) ? 'Sí' : 'No'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bottom Info */}
                                        <div className="border-top pt-2">
                                            <div className="small mb-1">
                                                <span className="text-muted">Empresa:</span> <span className="fw-bold">{discount.companyRuc || '-'}</span>
                                            </div>
                                            <div className="small">
                                                <span className="text-muted">Expira:</span> <span className="fw-bold">{formatDate(discount.expiresAt)}</span>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
                </>
            ) : null}

            <DiscountFormModal
                show={showModal}
                handleClose={closeModal}
                initialData={initialData}
                onSuccess={refetch}
            />
        </Container>
    );
};

export default DiscountListScreen;
